import React, { useRef, useMemo } from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { PrimaryButton } from "../buttons/PrimaryButton";
import {
  Tooltip,
  Menu,
  MenuHandler,
  MenuList,
  MenuItem,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import InformationIcon from "../../assets/icons/info/information.png";

dayjs.extend(weekOfYear);
dayjs.extend(quarterOfYear);

// Suppress ResizeObserver errors
if (typeof window !== "undefined") {
  const errorHandler = (e: ErrorEvent) => {
    if (
      e.message.includes("ResizeObserver loop") ||
      e.message.includes(
        "ResizeObserver loop completed with undelivered notifications"
      )
    ) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return true;
    }
    return false;
  };
  window.addEventListener("error", errorHandler);
}

interface IConversionsDataPoint {
  period: string;
  date: string;
  percentage_active_plastic_over_total: number;
  percentage_plastic_not_active_over_total: number;
  percentage_material_no_plastic_over_total: number;
  percentage_statie_only_over_total: number;
  percentage_statie_pro_over_total?: number;
  statie_only_no_pro?: number;
  week_start?: string;
  week_end?: string;
  month_start?: string;
  year?: number;
  quarter?: string;
}

interface Props {
  conversionsData: IConversionsDataPoint[] | null;
  isLoading: boolean;
  period: "weekly" | "monthly" | "quarterly" | "yearly";
  setPeriod: (period: "weekly" | "monthly" | "quarterly" | "yearly") => void;
  year: number;
  setYear: (year: number) => void;
  fromMonth: number;
  setFromMonth: (month: number) => void;
  toMonth: number;
  setToMonth: (month: number) => void;
  fromYear: number;
  setFromYear: (year: number) => void;
  fromWeek: number;
  setFromWeek: (week: number) => void;
  toYear: number;
  setToYear: (year: number) => void;
  toWeek: number;
  setToWeek: (week: number) => void;
}

export const PlasticConversions: React.FC<Props> = ({
  conversionsData,
  isLoading,
  period,
  setPeriod,
  year,
  setYear,
  fromMonth,
  setFromMonth,
  toMonth,
  setToMonth,
  fromYear,
  setFromYear,
  fromWeek,
  setFromWeek,
  toYear,
  setToYear,
  toWeek,
  setToWeek,
}) => {
  const snapshotRef = useRef(null);
  const [, takeScreenshot] = useScreenshot();

  const downloadScreenshot = () => {
    takeScreenshot(snapshotRef.current).then((image: any) => {
      const link = document.createElement("a");
      link.href = image;
      link.download = "plastic_conversions.png";
      link.click();
    });
  };

  // Colors matching the Droppers chart
  const colors = ["#FE7349", "#F8A991", "#BFDFF7", "#19A1B9", "#037887"];

  // Prepare chart data
  const { categories, series } = useMemo(() => {
    if (!conversionsData || conversionsData.length === 0) {
      return { categories: [], series: [] };
    }

    const cats = conversionsData.map((point) => {
      if (period === "weekly") {
        // Use week_start date if available, otherwise use date
        const dateToFormat = point.week_start || point.date;
        return dayjs(dateToFormat).format("MMM D, YYYY");
      } else if (period === "monthly") {
        return dayjs(point.date).format("YYYY-MM");
      } else if (period === "quarterly") {
        return `${point.year}-Q${point.quarter}`;
      } else {
        return String(point.year);
      }
    });

    // Data in order: Active Plastic, Plastic Not Active, Material No Plastic, Statie Only (no Pro), Statie Pro
    // Backend already returns percentages (0-100), so no need to multiply by 100
    const activePlasticData = conversionsData.map((p) =>
      Number(p.percentage_active_plastic_over_total.toFixed(1))
    );
    const plasticNotActiveData = conversionsData.map((p) =>
      Number(p.percentage_plastic_not_active_over_total.toFixed(1))
    );
    const materialNoPlasticData = conversionsData.map((p) =>
      Number(p.percentage_material_no_plastic_over_total.toFixed(1))
    );
    const statieOnlyData = conversionsData.map((p) => {
      const rawStatieOnly = p.percentage_statie_only_over_total ?? 0;
      const statiePro = p.percentage_statie_pro_over_total ?? 0;
      const statieOnlyNoPro =
        p.statie_only_no_pro ?? Number((rawStatieOnly - statiePro).toFixed(1));
      return Number(statieOnlyNoPro.toFixed(1));
    });
    const statieProData = conversionsData.map((p) =>
      Number((p.percentage_statie_pro_over_total ?? 0).toFixed(1))
    );

    return {
      categories: cats,
      series: [
        { name: "Active Plastic", data: activePlasticData },
        { name: "Plastic Not Active", data: plasticNotActiveData },
        { name: "Material No Plastic", data: materialNoPlasticData },
        { name: "Statie Only (no Pro)", data: statieOnlyData },
        { name: "Statie Pro", data: statieProData },
      ],
    };
  }, [conversionsData, period]);

  const options: ApexOptions = {
    chart: {
      type: "bar",
      stacked: true,
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 0,
        dataLabels: {
          total: {
            enabled: false,
          },
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: any) {
        // Only show label if percentage is significant (>3%)
        return val && val > 3 ? Math.round(val) + "%" : "";
      },
      style: {
        fontSize: "12px",
        colors: ["#000"],
      },
    },
    xaxis: {
      categories: categories,
      labels: {
        rotate: -45,
        rotateAlways: true,
        trim: false,
      },
    },
    yaxis: {
      min: 0,
      max: 100,
      tickAmount: 5,
      labels: {
        formatter: function (val: number) {
          return val.toFixed(0) + "%";
        },
      },
    },
    colors: colors,
    fill: {
      opacity: 1,
    },
    legend: {
      position: "bottom",
      horizontalAlign: "center",
      offsetY: 10,
    },
    tooltip: {
      y: {
        formatter: function (val: number) {
          return val.toFixed(1) + "%";
        },
      },
    },
    grid: {
      strokeDashArray: 5,
      padding: {
        left: 10,
        right: 30,
        bottom: 10,
      },
    },
  };

  // Year options (2024..current year)
  const yearOptions = useMemo(
    () =>
      Array.from(
        { length: dayjs().year() - 2023 },
        (_, i) => dayjs().year() - i
      ),
    []
  );
  const fromYearOptions = yearOptions;
  const toYearOptions = yearOptions;

  const monthOptions = useMemo(
    () => [
      { value: 1, label: "Jan" },
      { value: 2, label: "Feb" },
      { value: 3, label: "Mar" },
      { value: 4, label: "Apr" },
      { value: 5, label: "May" },
      { value: 6, label: "Jun" },
      { value: 7, label: "Jul" },
      { value: 8, label: "Aug" },
      { value: 9, label: "Sep" },
      { value: 10, label: "Oct" },
      { value: 11, label: "Nov" },
      { value: 12, label: "Dec" },
    ],
    []
  );
  const fromMonthOptions = useMemo(() => {
    const cy = new Date().getFullYear();
    const cm = new Date().getMonth() + 1;
    return fromYear === cy
      ? monthOptions.filter((m) => m.value <= cm)
      : monthOptions;
  }, [fromYear, monthOptions]);
  const toMonthOptions = useMemo(() => {
    const cy = new Date().getFullYear();
    const cm = new Date().getMonth() + 1;
    return toYear === cy
      ? monthOptions.filter((m) => m.value <= cm)
      : monthOptions;
  }, [toYear, monthOptions]);

  const fromWeekOptions = useMemo(() => {
    const options = [];
    for (let w = 1; w <= 53; w++) {
      const weekStart = dayjs().year(fromYear).isoWeek(w).startOf("isoWeek");
      options.push({
        value: w,
        label: `Week ${w} (${weekStart.format("MMM D")})`,
        disabled: false,
      });
    }
    return options;
  }, [fromYear]);

  const toWeekOptions = useMemo(() => {
    const options = [];
    for (let w = 1; w <= 53; w++) {
      const weekStart = dayjs().year(toYear).isoWeek(w).startOf("isoWeek");
      options.push({
        value: w,
        label: `Week ${w} (${weekStart.format("MMM D")})`,
        disabled: false,
      });
    }
    return options;
  }, [toYear]);

  return (
    <div className="flex flex-col w-full max-w-full overflow-hidden rounded-xl p-4 sm:p-6 bg-white border-gray-border drop-shadow-none mb-6">
      <div className="flex flex-col sm:flex-row w-full items-start justify-end">
        <div className="flex flex-col sm:flex-row items-center justify-start sm:justify-end gap-4">
          {/* Period */}
          <div className="flex items-center gap-2">
            <label className="font-satoshi text-sm text-gray-text">
              Period:
            </label>
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {period === "weekly"
                    ? "Weekly"
                    : period === "monthly"
                      ? "Monthly"
                      : period === "quarterly"
                        ? "Quarterly"
                        : "Yearly"}
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                <MenuItem
                  disabled={period === "weekly"}
                  onClick={() => setPeriod("weekly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Weekly
                </MenuItem>
                <MenuItem
                  disabled={period === "monthly"}
                  onClick={() => setPeriod("monthly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Monthly
                </MenuItem>
                <MenuItem
                  disabled={period === "quarterly"}
                  onClick={() => setPeriod("quarterly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Quarterly
                </MenuItem>
                <MenuItem
                  disabled={period === "yearly"}
                  onClick={() => setPeriod("yearly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Yearly
                </MenuItem>
              </MenuList>
            </Menu>
          </div>

          {/* Monthly: From Year/Month + To Year/Month */}
          {period === "monthly" && (
            <>
              <div className="flex items-center gap-2">
                <label className="font-satoshi text-sm text-gray-text">
                  From:
                </label>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {fromYear}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {fromYearOptions.map((y: number) => (
                      <MenuItem
                        key={y}
                        disabled={fromYear === y}
                        onClick={() => setFromYear(y)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {y}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {fromMonthOptions.find((m) => m.value === fromMonth)
                        ?.label || "Month"}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {fromMonthOptions.map((m) => (
                      <MenuItem
                        key={m.value}
                        disabled={fromMonth === m.value}
                        onClick={() => setFromMonth(m.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {m.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </div>
              <div className="flex items-center gap-2">
                <label className="font-satoshi text-sm text-gray-text">
                  To:
                </label>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {toYear}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {toYearOptions.map((y: number) => (
                      <MenuItem
                        key={y}
                        disabled={toYear === y}
                        onClick={() => setToYear(y)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {y}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {toMonthOptions.find((m) => m.value === toMonth)?.label ||
                        "Month"}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {toMonthOptions.map((m) => (
                      <MenuItem
                        key={m.value}
                        disabled={toMonth === m.value}
                        onClick={() => setToMonth(m.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {m.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </div>
            </>
          )}

          {/* Weekly: From Year + Week + To Year + Week */}
          {period === "weekly" && (
            <>
              <div className="flex items-center gap-2">
                <label className="font-satoshi text-sm text-gray-text">
                  From:
                </label>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {fromYear}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {fromYearOptions.map((y: number) => (
                      <MenuItem
                        key={y}
                        disabled={fromYear === y}
                        onClick={() => setFromYear(y)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {y}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <Menu>
                  <MenuHandler>
                    <button
                      className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                      title={
                        fromWeekOptions.find((w: any) => w.value === fromWeek)
                          ?.label
                      }
                    >
                      {fromWeekOptions.find((w: any) => w.value === fromWeek)
                        ?.label || "Week"}
                      <ChevronDown className="ml-2 flex-shrink-0" />
                    </button>
                  </MenuHandler>
                  <MenuList
                    className="rounded-none max-h-[250px] overflow-y-auto"
                    placeholder={undefined}
                  >
                    {fromWeekOptions.map((w: any) => (
                      <MenuItem
                        key={w.value}
                        disabled={w.disabled || fromWeek === w.value}
                        onClick={() => setFromWeek(w.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {w.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </div>
              <div className="flex items-center gap-2">
                <label className="font-satoshi text-sm text-gray-text">
                  To:
                </label>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {toYear}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {toYearOptions.map((y: number) => (
                      <MenuItem
                        key={y}
                        disabled={toYear === y}
                        onClick={() => setToYear(y)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {y}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <Menu>
                  <MenuHandler>
                    <button
                      className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                      title={
                        toWeekOptions.find((w: any) => w.value === toWeek)
                          ?.label
                      }
                    >
                      {toWeekOptions.find((w: any) => w.value === toWeek)
                        ?.label || "Week"}
                      <ChevronDown className="ml-2 flex-shrink-0" />
                    </button>
                  </MenuHandler>
                  <MenuList
                    className="rounded-none max-h-[250px] overflow-y-auto"
                    placeholder={undefined}
                  >
                    {toWeekOptions.map((w: any) => (
                      <MenuItem
                        key={w.value}
                        disabled={w.disabled || toWeek === w.value}
                        onClick={() => setToWeek(w.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {w.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </div>
            </>
          )}

          {/* Quarterly: Only Year (no From/To) */}
          {period === "quarterly" && (
            <div className="flex items-center gap-2">
              <label className="font-satoshi text-sm text-gray-text">
                Year:
              </label>
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                    {year}
                    <ChevronDown className="ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {yearOptions.map((y: number) => (
                    <MenuItem
                      key={y}
                      disabled={year === y}
                      onClick={() => setYear(y)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {y}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </div>
          )}

          {/* Yearly: From Year + To Year */}
          {period === "yearly" && (
            <>
              <div className="flex items-center gap-2">
                <label className="font-satoshi text-sm text-gray-text">
                  From:
                </label>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {fromYear}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {fromYearOptions.map((y: number) => (
                      <MenuItem
                        key={y}
                        disabled={fromYear === y}
                        onClick={() => setFromYear(y)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {y}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </div>
              <div className="flex items-center gap-2">
                <label className="font-satoshi text-sm text-gray-text">
                  To:
                </label>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {toYear}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {toYearOptions.map((y: number) => (
                      <MenuItem
                        key={y}
                        disabled={toYear === y}
                        onClick={() => setToYear(y)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {y}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </div>
            </>
          )}

          <PrimaryButton
            size="sm"
            onClick={downloadScreenshot}
            label="Download image"
          />
        </div>
      </div>

      {/* Chart */}
      <div className="mt-4" ref={snapshotRef}>
        <div className="flex items-center ">
          <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black">
            User Composition
          </p>
          <Tooltip
            content="Percentage breakdown of user types"
            placeholder={undefined}
          >
            <img
              src={InformationIcon}
              alt="info"
              className="inline-block w-4 h-4 ml-2 cursor-pointer"
            />
          </Tooltip>
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center h-[400px]">
            <p>Loading...</p>
          </div>
        ) : series.length > 0 ? (
          <Chart options={options} series={series} type="bar" height={450} />
        ) : (
          <div className="flex items-center justify-center h-[400px]">
            <p>No data available</p>
          </div>
        )}
      </div>
    </div>
  );
};
